<template>
    <div>
        <div class="is-flex is-align-items-center is-justify-content-space-between mb-4">
            <p class="heading has-text-grey-light mb-0">
                <span class="icon-text">
                    <span class="icon"><i class="fas fa-sitemap"></i></span>
                    <span>{{ translate("discipline_packages.bracket_ranges_section") }}</span>
                </span>
            </p>
            <DropdownButton v-if="presets.length > 0" :is-left="false">
                <template v-slot:trigger>
                    <button class="button is-small">
                        <span class="icon"><i class="fas fa-layer-group"></i></span>
                        <span>{{ translate("discipline_packages.load_preset") }}</span>
                        <span class="icon"><i class="fas fa-angle-down"></i></span>
                    </button>
                </template>
                <template v-slot:content>
                    <a v-for="preset in presets" :key="preset.name" class="dropdown-item" @click="applyPreset(preset)">
                        {{ preset.name }}
                    </a>
                </template>
            </DropdownButton>
        </div>

        <!-- Default catch-all row -->
        <div v-if="store.currentDefaultBracketType !== null" class="is-flex is-align-items-center mb-2" style="gap: 0.6rem">
            <div class="range-label">
                <span class="button is-static is-small">
                    {{ translate("discipline_packages.default_bracket_range") }}
                </span>
            </div>
            <div class="select is-small">
                <select disabled>
                    <option v-if="store.currentDefaultBracketTypeTranslationKey">
                        {{ translate(store.currentDefaultBracketTypeTranslationKey) }}
                    </option>
                </select>
            </div>
            <KataBracketSettings v-if="store.currentDefaultBracketType === BracketTypeEnum.Kata" readonly />
            <RepechageByFinalistsBracketSettings v-if="store.currentDefaultBracketType === BracketTypeEnum.RepechageByFinalists" readonly />
            <button class="button is-small is-danger" disabled>
                <span class="icon"><i class="fa fa-trash"></i></span>
            </button>
        </div>

        <!-- Configurable ranges -->
        <div
            class="is-flex is-align-items-center mb-2"
            style="gap: 0.6rem"
            v-for="(constraint, index) in store.activeConstraints"
            :key="index"
        >
            <div class="field has-addons range-label mb-0">
                <p class="control">
                    <span class="button is-static is-small">
                        {{ translate("admin_bracket_listing.bracket_range_from_to", false, [constraint.lowerLimit]) }}
                    </span>
                </p>
                <p class="control">
                    <input
                        class="input is-small range-upper-input"
                        type="number"
                        :min="constraint.lowerLimit"
                        v-model="constraint.upperLimit"
                        @change="store.adjustRanges(index)"
                    />
                </p>
            </div>

            <BracketTypeSelector
                :bracket-types="store.currentAllowedBracketTypes"
                :lower-limit="constraint.lowerLimit"
                :upper-limit="constraint.upperLimit"
                :disabled="!store.usesBracketRanges"
                v-model="constraint.bracketType"
            />

            <KataBracketSettings
                v-if="constraint.bracketType === BracketTypeEnum.Kata"
                :model-value="asKataSettings(constraint.bracketTypeSettings)"
                @update:model-value="constraint.bracketTypeSettings = $event"
            />
            <RepechageByFinalistsBracketSettings
                v-if="constraint.bracketType === BracketTypeEnum.RepechageByFinalists"
                :model-value="asRepechageByFinalistsSettings(constraint.bracketTypeSettings)"
                @update:model-value="constraint.bracketTypeSettings = $event"
            />

            <button class="button is-small is-danger" @click="store.removeRange(index)">
                <span class="icon"><i class="fa fa-times"></i></span>
            </button>
        </div>

        <div class="mt-3">
            <button class="button is-small" @click="store.addRange()">
                <span class="icon"><i class="fa fa-plus"></i></span>
                <span>{{ translate("discipline_packages.add_bracket_range") }}</span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../Common/translations";
    import { useDisciplinePackagesStore } from "./disciplinePackagesStore";
    import BracketTypeSelector from "../Brackets/BracketTypeSelector.vue";
    import DropdownButton from "../Common/DropdownButton.vue";
    import KataBracketSettings from "./BracketSettings/KataBracketSettings.vue";
    import RepechageByFinalistsBracketSettings from "./BracketSettings/RepechageByFinalistsBracketSettings.vue";
    import {
        getPresetsForDiscipline,
        BracketTypeConstraint,
        BracketTypeSettings,
        KataSettings,
        RepechageByFinalistsSettings,
    } from "../Brackets/configurationTemplates";
    import { BracketTypeEnum } from "../Common/types";

    const store = useDisciplinePackagesStore();

    // constraint.bracketTypeSettings is a union of every bracket type's settings shape;
    // narrowed here to whichever one applies for the constraint.bracketType branch being rendered.
    function asKataSettings(settings: BracketTypeSettings | null | undefined): KataSettings | undefined {
        return settings as KataSettings | undefined;
    }

    function asRepechageByFinalistsSettings(settings: BracketTypeSettings | null | undefined): RepechageByFinalistsSettings | undefined {
        return settings as RepechageByFinalistsSettings | undefined;
    }

    const presets = computed(() => {
        const discipline = store.selectedPackage?.discipline;
        if (!discipline) {
            return [];
        }
        return getPresetsForDiscipline(discipline);
    });

    function applyPreset(preset: { name: string; constraints: BracketTypeConstraint[] }): void {
        store.applyPreset(preset.constraints);
    }
</script>

<style scoped>
    .range-label {
        min-width: 180px;
    }

    .range-upper-input {
        width: 70px;
    }
</style>
